import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { Wrench } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const r = await register(email, password, name);
    setLoading(false);
    if (!r.ok) setErr(r.error);
    else nav("/pending-approval", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0C]">
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1713371398484-cc4e4f6a262a?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="CNC"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0C] via-transparent to-[#0A0A0C]" />
        <div className="relative z-10 h-full p-16 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF4F00] flex items-center justify-center">
              <Wrench weight="bold" size={22} className="text-black" />
            </div>
            <div>
              <div className="font-display font-black text-2xl leading-none">MTM</div>
              <div className="text-[11px] font-mono text-[#FF4F00] tracking-widest">MACHINE SHOP OPS</div>
            </div>
          </div>
          <div>
            <div className="caution-stripes w-40 h-1 mb-6" />
            <h1 className="font-display font-black text-5xl xl:text-6xl leading-none tracking-tight mb-4">
              REQUEST<br />
              <span className="text-[#FFD700]">ACCESS.</span>
            </h1>
            <p className="text-[#A1A1AA] max-w-md text-base">
              An admin will review your request. You'll be notified once approved.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// NEW OPERATOR</div>
            <h2 className="font-display font-black text-4xl mb-2">CREATE ACCOUNT</h2>
            <p className="text-[#A1A1AA] text-sm">Fill in your details. Admin approval required before first sign-in.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1 block">Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid={AUTH.registerName}
                className="w-full bg-[#121214] border border-[#27272A] px-4 py-3 focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
                placeholder="Jane Machinist"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={AUTH.registerEmail}
                className="w-full bg-[#121214] border border-[#27272A] px-4 py-3 focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
                placeholder="jane@machineshop.com"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1 block">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={AUTH.registerPassword}
                className="w-full bg-[#121214] border border-[#27272A] px-4 py-3 focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
                placeholder="At least 6 characters"
              />
            </div>
            {err && (
              <div
                data-testid={AUTH.registerError}
                className="border-l-4 border-[#D32F2F] bg-[#D32F2F]/10 px-4 py-3 text-sm"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid={AUTH.registerSubmit}
              className="brutal-btn w-full bg-[#FF4F00] hover:bg-[#FF6B2B] active:bg-[#E64700] text-black py-3 disabled:opacity-60 transition-colors duration-150"
            >
              {loading ? "Submitting..." : "Request Access →"}
            </button>
          </form>
          <div className="mt-6 text-sm text-[#A1A1AA]">
            Already registered?{" "}
            <Link data-testid={AUTH.gotoLogin} to="/login" className="text-[#FF4F00] hover:underline font-semibold">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
